"use client";

import Link from 'next/link';
import Image from 'next/image';
import { footerLinks, socialLinks } from '@/lib/data';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FadeIn } from '@/components/animations/FadeIn';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';

export function Footer() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, source: 'footer' }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Success!",
          description: "You've been subscribed to our newsletter!",
        });
        setEmail('');
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to subscribe. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative">
      {/* cta section */}
      <div className="absolute -top-24 md:-top-20 z-10 left-1/2 transform -translate-x-1/2 min-h-[12rem] md:h-40 w-[95%] md:w-4/5 max-w-4xl bg-gradient-to-tr to-background from-primary via-secondary border border-primary/30 rounded-2xl overflow-hidden shadow-2xl flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10"></div>
        <div className="relative container mx-auto px-4 md:px-8 py-6 md:py-5 flex flex-col md:flex-row items-center justify-between text-center md:text-left gap-4">
          <FadeIn>
            <div className="max-w-2xl">
              <h2 className="text-xl md:text-2xl font-bold font-headline mb-2 md:mb-4 text-white">
                Ready to Transform Your Business?
              </h2>
              <p className="text-sm text-white/90 leading-relaxed mb-0">
                Join 500+ businesses already saving 20+ hours per week with our AI solutions.
              </p>
            </div>
          </FadeIn>
          <Button asChild size="lg" className='flex-shrink-0'>
            <Link href="/contact">Get a Free Quote</Link>
          </Button>
        </div>
      </div>
      <div className='bg-background border-t border-border/40 pt-40 md:pt-32'>
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="space-y-4 md:col-span-2 lg:col-span-1"><Image
              src="/tellis_logo.png"
              alt="Tellis AI"
              width={100}
              height={100}
              priority
              className="w-24 h-auto"
            />
              <p className="text-muted-foreground text-sm max-w-xs">
                Empowering small and medium businesses with no-code AI solutions.
              </p>
              <div className="flex space-x-4 mt-4">
                {socialLinks.map((social) => (
                  <Link key={social.name} href={social.href} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary">
                    <social.icon className="h-5 w-5" />
                    <span className="sr-only">{social.name}</span>
                  </Link>
                ))}
              </div>
            </div>
            {footerLinks.map((section) => (
              <div key={section.title} className="space-y-4">
                <h3 className="font-headline font-semibold text-lg underline decoration-primary underline-offset-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <Link href={link.href} className="text-muted-foreground text-sm hover:text-primary hover:underline underline-offset-4">
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className="space-y-4">
              <h3 className="font-headline font-semibold text-lg underline decoration-primary underline-offset-4">Subscribe for AI Updates</h3>
              <p className="text-muted-foreground text-sm">Get the latest AI trends and how-to guides directly to your inbox.</p>
              <form onSubmit={handleNewsletterSubmit} className="flex">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-background rounded-r-none"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Button
                  type="submit"
                  className='rounded-l-none'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </form>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-center text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Tellis Technologies. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>

  );
}
